import { useState, type ReactNode } from 'react';
import { aiConsent, aiConsentSave, type AiConsent } from '@aithora/shared';
import { useSession } from '../session/SessionContext';
import { useQuery } from '../useQuery';
import { sx } from './designStyles';
import { Skeleton } from './states';

/**
 * The AI consent notice before Family first use Atlas on the console (Simon's review,
 * 2026-10-19), the phone's gate (apps/mobile features/consent/AiConsentGate.tsx) in its own
 * words: who gets what they write ("our third-party AI providers"), then Agree or Not now.
 * The answer is the same server record the phone keeps (GET and PUT /me/ai-consent), so an
 * Agree on either is an Agree on both. Until they agree nothing is shown and nothing is sent;
 * a record that cannot be read keeps the notice up (fail closed).
 */
const PROVIDERS = 'our third-party AI providers';
const LINES = [
  `Atlas sends what you ask and the details Atlas needs to answer to ${PROVIDERS} to make the reply.`,
  'Nothing is sent until you agree. A person still checks anything that changes care.',
];
const OFF = [`Atlas stays hidden and nothing is sent to ${PROVIDERS}.`, 'Turn it on when you’re ready.'];

const BTN = 'display:inline-flex;align-items:center;height:36px;padding:0 16px;border-radius:8px;font-size:13.5px;font-weight:700;cursor:pointer;white-space:nowrap;';

export function AiConsentGate({ children, panel = false, onClose }: { children: ReactNode; panel?: boolean; onClose?: () => void }) {
  const { client, session } = useSession();
  const q = useQuery<AiConsent>((signal) => aiConsent(client, { signal }), [client, session?.user.id]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  if (q.data?.decision === 'agreed') return <>{children}</>;
  const declined = q.data?.decision === 'declined';
  const save = async (agreed: boolean) => {
    setBusy(true);
    setErr(null);
    try {
      const r = await aiConsentSave(client, { body: { agreed, platform: 'web' } });
      q.setData(() => r);
    } catch {
      setErr('That did not save. Try again.');
    } finally {
      setBusy(false);
    }
  };
  const card = q.loading && !q.data && !q.error ? (
    <Skeleton rows={2} label="Loading" />
  ) : (
    <div data-testid="ai-consent" style={sx('display:flex;flex-direction:column;gap:8px;max-width:560px;padding:16px 18px;border-radius:12px;background:#FCFAF4;border:1px solid rgba(34,39,46,.14);')}>
      <div data-testid={declined ? 'ai-consent-off' : 'ai-consent-kicker'} style={sx('font-size:14px;font-weight:700;letter-spacing:.09em;text-transform:uppercase;color:#54606C;')}>
        {declined ? 'Atlas is off' : 'Before you use Atlas'}
      </div>
      <div role="heading" aria-level={2} style={sx('font-size:17px;font-weight:800;color:#22272E;')}>{declined ? 'You chose Not now' : 'Atlas uses AI'}</div>
      {(declined ? OFF : LINES).map((l) => (
        <div key={l} style={sx('font-size:13.5px;line-height:1.5;color:#3C454F;')}>{l}</div>
      ))}
      {err && <div role="alert" style={sx('font-size:13px;color:#B3261E;')}>{err}</div>}
      <div style={sx('display:flex;gap:8px;flex-wrap:wrap;margin-top:4px;')}>
        <button type="button" disabled={busy} onClick={() => void save(true)} style={sx(BTN + 'border:0;background:#22272E;color:#fff;')}>
          {declined ? 'Turn Atlas on' : 'Agree'}
        </button>
        {!declined && (
          <button type="button" disabled={busy} onClick={() => void save(false)} style={sx(BTN + 'background:transparent;color:#22272E;border:1px solid rgba(34,39,46,.2);')}>
            Not now
          </button>
        )}
      </div>
    </div>
  );
  if (!panel) return <div style={sx('display:flex;flex-direction:column;gap:12px;padding:8px 0;min-width:0;')}>{card}</div>;
  // In the top-bar drawer: the notice sits where the panel would, and closes the same way.
  return (
    <div role="dialog" aria-label="Atlas" style={sx('position:fixed;top:56px;right:12px;z-index:40;width:min(420px,calc(100vw - 24px));display:flex;flex-direction:column;gap:8px;')}>
      {card}
      {onClose && (
        <button type="button" onClick={onClose} style={sx(BTN + 'align-self:flex-end;background:transparent;color:#2F5C97;border:0;')}>
          Close
        </button>
      )}
    </div>
  );
}
